import React, { useState, useEffect } from 'react';
import { Key, Check, X, AlertCircle, Info } from 'lucide-react';
import '../styles/License.css';

function License() {
  const [machineId, setMachineId] = useState('');
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [licenseKey, setLicenseKey] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadLicenseStatus();
  }, []);

  const loadLicenseStatus = async () => {
    try {
      setLoading(true);
      
      // Get machine ID
      const machineResponse = await fetch('/api/license/machine-id');
      const machineData = await machineResponse.json();
      setMachineId(machineData.machineId);

      // Get license status
      const statusResponse = await fetch('/api/license/status');
      const statusData = await statusResponse.json();
      setLicenseStatus(statusData);
      setError(null);
    } catch (err) {
      setError('Failed to load license information: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateLicense = async (e) => {
    e.preventDefault();

    if (!licenseKey) {
      setError('Please enter a license key');
      return;
    }

    if (!company) {
      setError('Please enter company name');
      return;
    }

    if (!email) {
      setError('Please enter email address');
      return;
    }

    try {
      setActivating(true);
      console.log('Activating license:', { licenseKey, machineId, company, email });
      
      const response = await fetch('/api/license/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          licenseKey,
          machineId,
          company,
          email
        })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      const responseText = await response.text();
      console.log('Response body:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error('Invalid response from server: ' + responseText.substring(0, 100));
      }

      if (!response.ok) {
        throw new Error(data.error || 'Activation failed');
      }

      setSuccessMsg('License activated successfully! Restarting application...');
      
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      console.error('Activation error:', err);
      setError('Failed to activate license: ' + err.message);
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="license-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading License Information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="license-container">
      <div className="license-header">
        <div className="license-title">
          <Key size={32} />
          <h1>License Management</h1>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}

      {/* Machine ID Card */}
      <div className="license-card">
        <div className="card-header">
          <Info size={20} />
          <h3>System Information</h3>
        </div>
        <div className="machine-id-section">
          <label>Machine ID (Unique System Identifier)</label>
          <div className="machine-id-display">
            <code>{machineId}</code>
            <button 
              className="copy-btn"
              onClick={() => {
                navigator.clipboard.writeText(machineId);
                setSuccessMsg('Machine ID copied to clipboard');
                setTimeout(() => setSuccessMsg(null), 2000);
              }}
            >
              Copy
            </button>
          </div>
        </div>
      </div>

      {/* License Status */}
      {licenseStatus?.isValid ? (
        <div className="license-card licensed">
          <div className="card-header success">
            <Check size={20} />
            <h3>License Status: Active</h3>
          </div>
          <div className="license-details">
            <div className="detail-row">
              <span className="label">Company:</span>
              <span className="value">{licenseStatus.company}</span>
            </div>
            <div className="detail-row">
              <span className="label">Email:</span>
              <span className="value">{licenseStatus.email}</span>
            </div>
            <div className="detail-row">
              <span className="label">License Type:</span>
              <span className="value">{licenseStatus.licenseType}</span>
            </div>
            <div className="detail-row">
              <span className="label">Expires:</span>
              <span className="value">{new Date(licenseStatus.expiryDate).toLocaleDateString('en-IN')}</span>
            </div>
            <div className="detail-row">
              <span className="label">Days Remaining:</span>
              <span className="value days-count">{licenseStatus.daysRemaining} days</span>
            </div>
          </div>
          <p className="info-text">
            ✓ This system is licensed and activated. You can use all features without restrictions.
          </p>
        </div>
      ) : (
        <div className="license-card unlicensed">
          <div className="card-header warning">
            <AlertCircle size={20} />
            <h3>No Active License</h3>
          </div>
          <p className="warning-text">
            This system does not have an active license. Please activate a license key to use the full features of SSP Billing System.
          </p>
          
          {!showForm ? (
            <button 
              className="btn-primary"
              onClick={() => setShowForm(true)}
            >
              <Key size={18} />
              Activate License
            </button>
          ) : (
            <form onSubmit={handleActivateLicense} className="license-form">
              <div className="form-group">
                <label>License Key</label>
                <input
                  type="text"
                  value={licenseKey}
                  onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
                  placeholder="LIC-XXXXXXXXXXXXXXXX-XXXXXXXX"
                />
              </div>

              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Enter your company name"
                />
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email address"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-success" disabled={activating}>
                  {activating ? 'Activating...' : 'Activate License'}
                </button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => {
                    setShowForm(false);
                    setLicenseKey('');
                    setCompany('');
                    setEmail('');
                  }}
                  disabled={activating}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* License Info */}
      <div className="license-card info-card">
        <h3>License Information</h3>
        <div className="info-content">
          <h4>One System, One License Policy</h4>
          <ul>
            <li>Each license key can only be activated on a single system</li>
            <li>The system is identified by a unique Machine ID based on hardware</li>
            <li>Once activated, the license is tied to this system permanently</li>
            <li>License expires 1 year after activation</li>
            <li>You can contact support to transfer your license to a different system</li>
          </ul>
          
          <h4 style={{ marginTop: '20px' }}>License Key Format</h4>
          <p>Your license key should follow this format:</p>
          <code>LIC-XXXXXXXXXXXXXXXX-XXXXXXXX</code>
          
          <h4 style={{ marginTop: '20px' }}>Need a License?</h4>
          <p>Contact support at <strong>support@sspbilling.com</strong> to purchase a license for this system.</p>
        </div>
      </div>
    </div>
  );
}

export default License;
